import React, { useEffect, useContext } from 'react';
import { VehicleController } from 'core/repositories/VehicleController';
import { createGlobalState } from './createGlobalState';
import { Vehicle } from 'core/models/vehicle';
import { Blinker, ControlMode, EmergencyMode, Gear } from 'core/models/drive';

type VehicleControllerStatus = {
    accel: number;
    brake: number;
    steering: number;
    blinker: Blinker | null;
    gear: Gear;
    controlMode: ControlMode;
    emergencyMode: EmergencyMode;
}

const initValue: VehicleControllerStatus = {
    accel: 0,
    brake: 0,
    steering: 0,
    blinker: null,
    gear: Gear.P,
    controlMode: ControlMode.Auto,
    emergencyMode: EmergencyMode.Normal,
};

const { useWithState, useDispatch, useState } = createGlobalState((o, a: Partial<VehicleControllerStatus>) => ({ ...o, ...a }), initValue);

type VehicleControllerUpdater = {
    controller: VehicleController;
    vehicleId: Vehicle.Id;
}

const VehicleControllerContext = React.createContext<VehicleController | null>(null);

const VehicleControllerUpdater = (props: React.PropsWithChildren<VehicleControllerUpdater>) => {
    const { controller } = props;

    const setValue = useDispatch();

    useEffect(() => {
        const listener = (event: VehicleController.Event) => {
            switch (event.eventType) {
                case 'accel':
                    setValue({ accel: event.accel });
                    break;
                case 'brake':
                    setValue({ brake: event.brake });
                    break;
                case 'steering':
                    setValue({ steering: event.steering });
                    break;
                case 'blinker':
                    setValue({ blinker: event.blinker });
                    break;
                case 'gear':
                    setValue({ gear: event.gear });
                    break;
                case 'controlMode':
                    setValue({ controlMode: event.controlMode });
                    break;
                case 'emergency':
                    setValue({ emergencyMode: event.emergency });
                    break;
            }
        };
        controller.addEventListener(listener);

        return () => {
            controller.removeEventListener(listener);
        };
    }, [controller, setValue]);

    return (
        <VehicleControllerContext.Provider value={controller}>
            <React.Fragment>
                {props.children}
            </React.Fragment>
        </VehicleControllerContext.Provider>
    );
};

export function useVehicleController() {
    return useContext(VehicleControllerContext);
}

export function useWithVehicleController() {
    const withState = useWithState();

    return (controller: VehicleController, vehicleId: Vehicle.Id) =>
        (children: React.ReactNode) =>
            withState(
                <VehicleControllerUpdater controller={controller} vehicleId={vehicleId}>
                    {children}
                </VehicleControllerUpdater>
            );
}

export function useVehicleControllerStatus() {
    return useState();
}
